import { useState, useEffect } from "react";

// react-router components
import { useNavigate } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @material-ui core components
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Custom styles for DashboardNavbar
import {
  navbar,
  navbarContainer,
  navbarIconButton,
  navbarMobileMenu,
} from "examples/Navbars/DashboardNavbar/styles";

// Material Dashboard 2 React context
import { useMaterialUIController, setTransparentNavbar, setMiniSidenav } from "context";

// Auth
import { useAuth } from "context/AuthContext";
import { nhanVaiTro } from "lib/nhanVaiTro";

// Tour hướng dẫn
import { useTourContext } from "context/TourContext";
import { BAT_HUONG_DAN } from "lib/batHuongDan";

function DashboardNavbar({ absolute, light, isMini }) {
  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar, darkMode, pageTitle } = controller;
  const [openMenu, setOpenMenu] = useState(false);
  const navigate = useNavigate();
  const { user, vaiTro, logout } = useAuth();
  const { active: tourDangHoatDong, startActiveTour } = useTourContext();

  useEffect(() => {
    // Setting the navbar type
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    // A function that sets the transparent state of the navbar.
    function handleTransparentNavbar() {
      setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar);
    }

    /**
     The event listener that's calling the handleTransparentNavbar function when
     scrolling the window.
    */
    window.addEventListener("scroll", handleTransparentNavbar);

    // Call the handleTransparentNavbar function to set the state with the initial value.
    handleTransparentNavbar();

    // Remove event listener on cleanup
    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  const handleOpenMenu = (event) => setOpenMenu(event.currentTarget);
  const handleCloseMenu = () => setOpenMenu(false);

  const renderMenu = () => (
    <Menu
      anchorEl={openMenu}
      anchorReference={null}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      open={Boolean(openMenu)}
      onClose={handleCloseMenu}
      sx={{ mt: 2 }}
    >
      <MDBox px={2} py={1} minWidth="12rem">
        <MDTypography variant="button" fontWeight="medium" display="block">
          {user?.email}
        </MDTypography>
        <MDTypography variant="caption" color="text">
          {nhanVaiTro(vaiTro)}
        </MDTypography>
      </MDBox>
      <Divider sx={{ my: 0.5 }} />
      <MenuItem
        onClick={() => {
          handleCloseMenu();
          navigate("/ho-so");
        }}
      >
        Hồ sơ của tôi
      </MenuItem>
      {BAT_HUONG_DAN && (
        <MenuItem
          onClick={() => {
            handleCloseMenu();
            navigate("/huong-dan");
          }}
        >
          Hướng dẫn sử dụng
        </MenuItem>
      )}
      <MenuItem
        onClick={() => {
          handleCloseMenu();
          logout();
        }}
      >
        Đăng xuất
      </MenuItem>
    </Menu>
  );

  // Styles for the navbar icons
  const iconsStyle = ({ palette: { dark, white, text }, functions: { rgba } }) => ({
    color: () => {
      let colorValue = light || darkMode ? white.main : dark.main;

      if (transparentNavbar && !light) {
        colorValue = darkMode ? rgba(text.main, 0.6) : text.main;
      }

      return colorValue;
    },
  });

  return (
    <AppBar
      position={absolute ? "absolute" : navbarType}
      color="inherit"
      sx={(theme) => navbar(theme, { transparentNavbar, absolute, light, darkMode })}
    >
      <Toolbar sx={(theme) => navbarContainer(theme)}>
        {isMini ? null : (
          <IconButton
            size="small"
            disableRipple
            color="inherit"
            sx={navbarMobileMenu}
            aria-label={miniSidenav ? "Mở menu" : "Đóng menu"}
            onClick={handleMiniSidenav}
          >
            <Icon sx={iconsStyle} fontSize="medium">
              {miniSidenav ? "menu" : "close"}
            </Icon>
          </IconButton>
        )}
        <MDBox color="inherit" minWidth={0} flexGrow={1} pr={1}>
          {pageTitle && (
            <MDTypography
              variant="h6"
              fontWeight="bold"
              color={light ? "white" : "dark"}
              noWrap
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                lineHeight: 1.3,
                fontSize: { xs: "0.95rem", md: "1.125rem" },
              }}
            >
              {pageTitle}
            </MDTypography>
          )}
        </MDBox>
        {isMini ? null : (
          <MDBox
            sx={{
              color: light ? "white" : "inherit",
              display: "flex",
              alignItems: "center",
              flexShrink: 0,
            }}
          >
            {BAT_HUONG_DAN && tourDangHoatDong?.steps?.length > 0 && (
              <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarIconButton}
                aria-label="Xem lại hướng dẫn trang này"
                onClick={startActiveTour}
                disabled={!!tourDangHoatDong?.run}
              >
                <Icon sx={iconsStyle}>help_outline</Icon>
              </IconButton>
            )}
            <IconButton
              size="small"
              disableRipple
              color="inherit"
              sx={navbarIconButton}
              aria-controls="account-menu"
              aria-haspopup="true"
              onClick={handleOpenMenu}
            >
              <Icon sx={iconsStyle}>account_circle</Icon>
            </IconButton>
            {renderMenu()}
          </MDBox>
        )}
      </Toolbar>
    </AppBar>
  );
}

// Setting default values for the props of DashboardNavbar
DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
};

// Typechecking props for the DashboardNavbar
DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

export default DashboardNavbar;
